import { useEffect, useState } from "react";
import { FormState, useFormContext } from "react-hook-form";
import AppIcon from "@/app/components/atomic/AppIcon";
import AppTooltipWrapper from "@/app/components/atomic/molecules/AppTooltipWrapper";

interface AppCheckboxProps {
    value: boolean | number | undefined | null;
    onChange: (inputReturn: boolean) => void;
    fieldName: string;
    className?: string;
    inputClassName?: string;
    /** Override the label font/weight classes (e.g. "font-normal" to drop the card variant's bold) */
    labelClassName?: string;
    /** Override default checkbox box bg/border classes when checked (e.g. "bg-rm-gray-light border-rm-gray") */
    boxClassName?: string;
    disabled?: boolean;
    children?: React.ReactNode;
    /** FontAwesome icon class to show between checkbox and label text */
    icon?: string;
    /** Secondary text shown below the label (card variant) */
    description?: React.ReactNode;
    /** Pill/badge shown next to the label (card variant) */
    tag?: React.ReactNode;
    /** Tooltip to show after the label text */
    tooltip?: {
        title?: string;
        text: string;
    };
    /** Use the loan-comparator design */
    variant?: 'default' | 'loan' | 'card';
}

function AppCheckbox({
    value = null,
    onChange,
    fieldName,
    className,
    inputClassName,
    labelClassName,
    boxClassName,
    disabled = false,
    children = null,
    icon,
    description,
    tag,
    tooltip,
    variant = 'default',
}: AppCheckboxProps) {
    const [error, setError] = useState(null)
    const [isFocused, setIsFocused] = useState(false);

    // useFormContext returns null when used outside FormProvider (react-hook-form v7+)
    const formContext = useFormContext() as { formState: FormState<any>; setValue: Function } | null;

    const formSetValue = formContext?.setValue || (() => {});

    useEffect(() => {
        formSetValue(fieldName, value)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChangeWithValidation = (inputReturn: boolean) => {
        if (disabled) return;
        setError(null)
        formSetValue(fieldName, inputReturn)
        onChange(inputReturn)
    }

    const isLoan = variant === 'loan';
    const isCard = variant === 'card';
    const isChecked = value === true;

    // Checkbox box border/bg colors
    const getCheckboxBoxClasses = () => {
        if (boxClassName && isChecked) return boxClassName;
        if (isCard) {
            if (isChecked && disabled) return 'border-gray-400 bg-gray-400 cursor-not-allowed';
            if (isChecked) return 'border-blue-600 bg-blue-600';
            if (disabled) return 'border-gray-200 bg-[#FAFAFA] cursor-not-allowed';
            return 'border-gray-300 bg-white';
        }
        if (isLoan) {
            if (disabled) {
                return 'border-[#B3B3B3] bg-[#FAFAFA] cursor-not-allowed';
            }
            if (isChecked) {
                return 'border-[#5CB846] bg-[rgba(92,184,92,0.2)]';
            }
            return 'border-[#909090] bg-white';
        }
        return isChecked ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-white';
    };

    // Check icon color
    const getCheckIconClass = () => {
        if (isLoan) {
            if (disabled) return 'fa-check text-[#808080]';
            return 'fa-check text-[#5CB846] font-semibold';
        }
        return 'fa-check text-white font-semibold';
    };

    const checkboxBox = (
        <div
            className={`relative flex items-center justify-center rounded border
                cursor-pointer transition-all shrink-0
                h-[20px] w-[20px] min-w-[20px]
                ${disabled && !isLoan && !isCard ? 'opacity-50 cursor-not-allowed' : ''}
                ${getCheckboxBoxClasses()}
                ${inputClassName || ''}`}
            onClick={isCard ? undefined : () => onChangeWithValidation(!value)}
        >
            {isChecked && <AppIcon iconClassName={getCheckIconClass()} type="regular" />}
            <input
                type="checkbox"
                id={fieldName}
                name={fieldName}
                className={`sr-only ${disabled ? 'cursor-not-allowed' : ''}`}
                checked={isChecked}
                onChange={(e) => onChangeWithValidation(e.target.checked)}
                disabled={disabled}
            />
        </div>
    );

    const labelContent = (
        <span className="flex items-center gap-2">
            {icon && (
                <i className={`fas fa-${icon} ${isLoan ? 'text-[16px] leading-[16px] mr-[2px]' : 'text-gray-500'}`} />
            )}
            {children}
        </span>
    );

    // Label text color and weight
    const getLabelClasses = () => {
        if (isLoan) {
            if (disabled) return 'text-[#666] font-semibold cursor-not-allowed';
            if (isChecked) return 'text-[#093e78] font-semibold cursor-pointer';
            return 'text-blue-700 font-medium cursor-pointer';
        }
        if (disabled) return 'text-gray-400 cursor-not-allowed';
        return 'text-blue-900 cursor-pointer';
    };

    if (isCard) {
        const cardState = disabled
            ? 'border-gray-200 bg-[#FAFAFA] cursor-not-allowed'
            : isChecked
                ? 'border-blue-500 bg-blue-50 cursor-pointer'
                : 'border-gray-200 bg-white cursor-pointer hover:border-gray-300';

        return (
            <div
                className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${cardState} ${className || ''}`}
                onClick={() => onChangeWithValidation(!value)}
            >
                {checkboxBox}

                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-[15px] ${labelClassName ?? 'font-bold'} ${disabled ? (isChecked ? 'text-gray-700' : 'text-gray-400') : 'text-pale'}`}>{children}</span>
                        {tag && (
                            <span className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-normal text-gray-600">
                                {tag}
                            </span>
                        )}
                    </div>
                    {description && (
                        <p className="m-0 mt-1 text-[13px] leading-[1.4em] text-gray-500">{description}</p>
                    )}
                </div>

                {error && <span className="text-red-500 text-xs ml-2">{error}</span>}
            </div>
        )
    }

    return (
        <div className={`flex items-center ${className || ''}`}>
            {checkboxBox}

            <label
                htmlFor={fieldName}
                className={`ml-[10px] text-[14px] ${getLabelClasses()} ${labelClassName || ''}`}
            >
                {tooltip ? (
                    <AppTooltipWrapper
                        id={`checkbox-tooltip-${fieldName}`}
                        title={tooltip.title}
                        text={tooltip.text}
                        iconClassName="fa-info-circle"
                        iconType="solid"
                        theme={isLoan ? 'loan' : 'dark'}
                    >
                        {labelContent}
                    </AppTooltipWrapper>
                ) : (
                    labelContent
                )}
            </label>

            {error && <span className="text-red-500 text-xs ml-2">{error}</span>}
        </div>
    )
}

export default AppCheckbox;
